import { getTodoTypes, updateTodoType, deleteTodoType, generateUploadConfig } from '@/api/todo';
import { AccountModule } from '@/store/modules/account';
import moment from 'moment';
import { calculateTableRowSpan, generateUUID, mapToObj } from '@/utils/other';

type ITodoType = ITodo.IResponse.ITodoType;
type ITodoTypeRow = ITodo.ITodoTypeRow;
type IOptionInfo = ITodo.IOptionInfo;
type IUpdateTodoType = ITodo.IRequest.IUpdateTodoType;
type ITodoCategory = ITodo.IRequest.ITodoCategory;

interface ITodoTypeManage {
  data: ITodoType[];
  tableData: ITodoTypeRow[];
  editTodoType: ITodoTypeRow | undefined;
  requestGetTodoTypes(): Promise<string>;
  requestUpdateTodoType(todoType: IUpdateTodoType): Promise<string>;
  requestDelTodoType(todoTypeId: string): Promise<string>;
  createTodoTypeRow(): ITodoTypeRow;
  updateTodoType(todoType: ITodoType): void;
  addTodoType(todoType: ITodoType): void;
  deleteTodoType(todoTypeId: string): void;
}
export interface IOption {
  value: string;
  label: string;
  children?: IOption[];
}

export interface IForm {
  name: string;
  projectId: string;
  projectName: string;
  active: boolean;
}
export interface ITreeData {
  key: string;
  name: string;
  value: number | null;
  formatType: number;
  children: ITreeData[];
  position: string;
}

export class TodoTypeManageClass implements ITodoTypeManage {
  private $data: ITodoType[] = [];
  private $editTodoType: ITodoTypeRow[] = [];
  get data() {
    return this.$data;
  }
  get tableData(): ITodoTypeRow[] {
    return sortByProjectId(convertDataToTable(this.$data));
  }
  get cascadedData() {
    return generateCascadedData(this.tableData);
  }
  get editTodoType() {
    return this.$editTodoType[0];
  }
  get uploadConfig() {
    return generateUploadConfig();
  }
  public requestGetTodoTypes(): Promise<string> {
    return new Promise((resolve, reject) => {
      getTodoTypes()
        .then((res) => {
          this.$data = res.data;
          resolve(res.message);
        })
        .catch((err) => {
          reject(err.message);
        });
    });
  }
  public requestUpdateTodoType(
    todoType: ITodo.IRequest.IUpdateTodoType,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      updateTodoType(todoType)
        .then((res1) => {
          resolve(res1.message);
        })
        .catch((err) => {
          reject(err.message);
        });
    });
  }
  public requestDelTodoType(todoTypeId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      deleteTodoType(todoTypeId)
        .then((res) => {
          this.deleteTodoType(todoTypeId);
          resolve(res.message);
        })
        .catch((err) => {
          reject(err.message);
        });
    });
  }
  public updateTodoType(todoType: ITodoType) {
    for (let typeItem of this.$data) {
      if (typeItem.id === todoType.id) {
        typeItem = { ...todoType };
        todoType.createdOn = todoType.createdOn || moment().toJSON();
        break;
      }
    }
  }
  public createTodoTypeRow(): ITodoTypeRow {
    const newTodoTypeId = generateUUID();
    const result: ITodoTypeRow = {
      id: newTodoTypeId,
      name: '',
      active: true,
      createdOn: '',
      createdBy: AccountModule.userDetailInfo?.id || '',
      createdName: AccountModule.name,
      projectName: '',
      projectId: '',
      firstCategoryId: generateUUID(),
      firstCategoryName: '',
      secondCategoryId: generateUUID(),
      secondCategoryName: '',
      thirdCategoryId: generateUUID(),
      thirdCategoryName: '',
      typeValue: null,
      formatType: 0,
      rowKey: newTodoTypeId + '/0',
    };
    this.$editTodoType[0] = result;
    return result;
  }
  public addTodoType(todoType: ITodoType) {
    this.$data.unshift(todoType);
  }
  public deleteTodoType(todoTypeId: string) {
    const index = this.$data.findIndex(
      (todoType) => todoType.id === todoTypeId,
    );
    this.$data.splice(index, 1);
  }
  public clearData() {
    this.$data.length = 0;
  }
  public getTodoTypeValueById(id: string) {
    return this.getTodoTypeById(id)?.typeValue;
  }
  public getTodoTypeById(id: string) {
    return this.tableData.find((typeItem) => {
      return [typeItem.id, typeItem.firstCategoryId, typeItem.secondCategoryId, typeItem.thirdCategoryId].includes(id);
    });
  }
}
export const todoTypeManage = new TodoTypeManageClass();

export const dataFlattening = (todoType: ITodoType): ITodoTypeRow[] => {
  const { category, ...baseInfo } = todoType;
  const result: ITodoTypeRow[] = [];
  if (!category || category.length === 0) {
    result.push({
      ...baseInfo,
      firstCategoryId: `${baseInfo.id}/1`,
      firstCategoryName: '/',
      secondCategoryId: `${baseInfo.id}/2`,
      secondCategoryName: '/',
      thirdCategoryId: `${baseInfo.id}/3`,
      thirdCategoryName: '/',
      typeValue: null,
      formatType: 0,
      rowKey: `${baseInfo.id}/0`,
    });
    return result;
  }
  category.forEach((categoryItem, index) => {
    result.push({
      ...baseInfo,
      firstCategoryId: categoryItem.items[0].id,
      firstCategoryName: categoryItem.items[0].name,
      secondCategoryId: categoryItem.items[1].id,
      secondCategoryName: categoryItem.items[1].name,
      thirdCategoryId: categoryItem.items[2].id,
      thirdCategoryName: categoryItem.items[2].name,
      typeValue: categoryItem.value,
      formatType: categoryItem.formatType > 0 ? categoryItem.formatType : 0,
      rowKey: `${baseInfo.id}/${index}`,
    });
  });
  return result;
};
export const convertDataToTable = (data: ITodoType[]) => {
  const todoTypeList: ITodoTypeRow[] = [];
  data.forEach((todoType) => {
    todoTypeList.push(...dataFlattening(todoType));
  });
  return todoTypeList;
};
type Key =
  | 'name'
  | 'projectId'
  | 'firstCategoryName'
  | 'secondCategoryName'
  | 'thirdCategoryName'
  | 'projectName';
export const generateOptionsByKey = (
  dataSource: ITodoTypeRow[],
  valueKey: Key,
  textKey: Key,
): IOptionInfo[] => {
  const result: IOptionInfo[] = [];
  const values: Array<string | number> = [];
  for (const item of dataSource) {
    if (!values.includes(item[valueKey])) {
      result.push({
        text: item[textKey] || item[valueKey],
        value: item[valueKey],
      });
      values.push(item[valueKey]);
    }
  }
  return result;
};
export const generateTableFilter = (
  data: ITodoTypeRow[],
): ITodo.ITodoTypeTableFilterOption => {
  return {
    nameOption: generateOptionsByKey(data, 'name', 'name'),
    projectOption: generateOptionsByKey(data, 'projectId', 'projectName'),
    firstCategoryOption: generateOptionsByKey(
      data,
      'firstCategoryName',
      'firstCategoryName',
    ),
    secondCategoryOption: generateOptionsByKey(
      data,
      'secondCategoryName',
      'secondCategoryName',
    ),
    thirdCategoryOption: generateOptionsByKey(
      data,
      'thirdCategoryName',
      'thirdCategoryName',
    ),
  };
};
export const createCustomRenderObj = (
  text: any,
  row: ITodoTypeRow,
  index: number,
  dataKey: keyof ITodoTypeRow,
  tableData: ITodoTypeRow[],
) => {
  const obj = {
    children: text,
    attrs: {
      rowSpan: 1,
    },
  };
  obj.attrs.rowSpan = calculateTableRowSpan(row, tableData, dataKey, index);
  return obj;
};

export const sortByProjectId = (data: ITodoTypeRow[]) => {
  const newArr = data.slice(0);
  const projectIds = [...new Set(data.map((type) => type.projectId))];
  const sorter = (a: ITodoTypeRow, b: ITodoTypeRow) => {
    return (
      projectIds.indexOf(a.projectId) - projectIds.indexOf(b.projectId)
    );
  };
  return newArr.sort(sorter);
};



const generateOptionListMap = (
  dataSource: ITodoTypeRow[],
  key: keyof ITodoTypeRow,
  labelKey: keyof ITodoTypeRow,
  valueKey: keyof ITodoTypeRow,
): Map<string, IOption[]> => {
  const mapStore = new Map<string, IOption[]>();
  for (const item of dataSource) {
    const itemKeyValue = item[key] as string;
    const labelKeyValue = item[labelKey] as string;
    const valueKeyValue = item[valueKey] as string;
    if (!labelKeyValue || labelKeyValue === '/') {
      continue;
    }
    if (mapStore.has(itemKeyValue)) {
      const values: string[] = mapStore
        .get(itemKeyValue)!
        .map((option) => option.value);
      if (!values.includes(valueKeyValue)) {
        mapStore.get(itemKeyValue)!.push({
          label: labelKeyValue,
          value: valueKeyValue,
        });
        values.push(valueKeyValue);
      }
    } else {
      mapStore.set(itemKeyValue, [
        {
          label: labelKeyValue,
          value: valueKeyValue,
        },
      ]);
    }
  }
  return mapStore;
};
export const generateCascadedData = (
  data: ITodoTypeRow[],
) => {
  const projectMap = generateOptionListMap(
    data,
    'projectId',
    'name',
    'id',
  );
  const typeMap = generateOptionListMap(
    data,
    'id',
    'firstCategoryName',
    'firstCategoryId',
  );
  const firstCategoryMap = generateOptionListMap(
    data,
    'firstCategoryId',
    'secondCategoryName',
    'secondCategoryId',
  );
  const secondCategoryMap = generateOptionListMap(
    data,
    'secondCategoryId',
    'thirdCategoryName',
    'thirdCategoryId',
  );
  const merageMap = (
    originalMap: Map<string, IOption[]>,
    meragedMap: Map<string, IOption[]>,
  ) => {
    for (const optionArr of originalMap.values()) {
      for (const option of optionArr) {
        if (!meragedMap.has(option.value)) {
          continue;
        }
        option.children = meragedMap.get(option.value);
      }
    }
  };
  merageMap(firstCategoryMap, secondCategoryMap);
  merageMap(typeMap, firstCategoryMap);
  merageMap(projectMap, typeMap);
  return mapToObj(projectMap);
};


const generateTreeDataMap = (
  dataSource: ITodoTypeRow[],
  key: keyof ITodoTypeRow,
  childKey: keyof ITodoTypeRow,
  nameKey: keyof ITodoTypeRow,
  valueKey: keyof ITodoTypeRow,
  formatTypeKey: keyof ITodoTypeRow,
): Map<string, ITreeData[]> => {
  const mapStore = new Map<string, ITreeData[]>();
  dataSource.forEach((rowItem) => {
    const itemKeyValue = rowItem[key] as string;
    const childKeyValue = rowItem[childKey] as string;
    const nameKeyValue = rowItem[nameKey] as string;
    const valueKeyValue = rowItem[valueKey] as number | null;
    const formatTypeValue = rowItem[formatTypeKey] as number;
    if (!mapStore.has(itemKeyValue)) {
      mapStore.set(itemKeyValue, [
        {
          key: childKeyValue,
          position: '',
          name: nameKeyValue,
          value: valueKeyValue,
          children: [],
          formatType: formatTypeValue,
        },
      ]);
    } else {
      const values: string[] = mapStore
        .get(itemKeyValue)!
        .map((option) => option.key);
      if (!values.includes(childKeyValue)) {
        mapStore.get(itemKeyValue)!.push({
          key: childKeyValue,
          position: '',
          name: nameKeyValue,
          value: valueKeyValue,
          children: [],
          formatType: formatTypeValue,
        });
        values.push(childKeyValue);
      }
    }
  });
  return mapStore;
};

export const generateTreeData = (
  originData: ITodoTypeRow[],
  typeRow: ITodoTypeRow,
): ITreeData[] => {
  const typeRowList = originData.filter(
    (typeRowItem) => typeRowItem.id === typeRow.id,
  );

  const firstCategoryTreeNodeMap = generateTreeDataMap(
    typeRowList,
    'firstCategoryId',
    'secondCategoryId',
    'secondCategoryName',
    'typeValue',
    'formatType',
  );
  const secondCategorTreeNodeMap = generateTreeDataMap(
    typeRowList,
    'secondCategoryId',
    'thirdCategoryId',
    'thirdCategoryName',
    'typeValue',
    'formatType',
  );
  const merageMap = (
    originalMap: Map<string, ITreeData[]>,
    meragedMap: Map<string, ITreeData[]>,
  ) => {
    for (const children of originalMap.values()) {
      for (const child of children) {
        child.children = meragedMap.get(child.key) || [];
      }
    }
  };
  merageMap(firstCategoryTreeNodeMap, secondCategorTreeNodeMap);
  const result: ITreeData[] = [];
  const resultKeys: string[] = [];
  typeRowList.forEach((rowItem) => {
    if (!resultKeys.includes(rowItem.firstCategoryId)) {
      let children: ITreeData[] = [];
      const secondCategorTreeNode = firstCategoryTreeNodeMap.get(
        rowItem.firstCategoryId,
      );
      if (secondCategorTreeNode) {
        children = secondCategorTreeNode.map((child) =>
          JSON.parse(JSON.stringify(child)),
        );
      }
      result.push({
        key: rowItem.firstCategoryId,
        position: '',
        name: rowItem.firstCategoryName,
        value: rowItem.typeValue,
        children,
        formatType: rowItem.formatType,
      });
      resultKeys.push(rowItem.firstCategoryId);
    }
  });
  enrichPositionForTreeData(result, '');
  return result;
};
const enrichPositionForTreeData = (originData: ITreeData[], prefix: string) => {
  originData.forEach((node, index) => {
    const position = prefix ? `${prefix}-${index}` : `${index}`;
    node.position = position;
    if (node.children.length > 0) {
      enrichPositionForTreeData(node.children, position);
    }
  });
};

export const convertTreeDataToCategory = (
  originData: ITreeData[],
): ITodoCategory[] => {
  // 从树形数据中找出所有children长度为0的节点，返回数组
  const findLastNode = (data: ITreeData[]): ITreeData[] => {
    const initLastNodes = [];
    for (const treeNode of data) {
      if (treeNode.children.length > 0) {
        initLastNodes.push(...findLastNode(treeNode.children));
      } else {
        initLastNodes.push(treeNode);
      }
    }
    return initLastNodes;
  };
  // 返回树形数据扁平化后的数组
  const flattenTreeData = (data: ITreeData[]): ITreeData[] => {
    const resultData: ITreeData[] = [];
    data.forEach((nodeItem) => {
      resultData.push(nodeItem);
      if (nodeItem.children.length > 0) {
        resultData.push(...flattenTreeData(nodeItem.children));
      }
    });
    return resultData;
  };
  // 遍历 children长度为0 的节点数组，生成Category 数组
  const generateCategoryTreeNodes = (
    nodes: ITreeData[],
    data: ITreeData[],
  ): ITreeData[][] => {
    const resultData: ITreeData[][] = [];
    nodes.forEach((nodeItem) => {
      const positions = nodeItem.position
        .split('-')
        .map((str) => Number(str));
      const treeNodes: ITreeData[] = [];
      let finded = data[positions[0]];
      if (finded) {
        treeNodes.push(finded);
      }
      positions.forEach((pos, index) => {
        // 从第二位开始
        if (index > 0) {
          finded = finded.children[pos];
          if (finded) {
            treeNodes.push(finded);
          }
        }
      });
      resultData.push(treeNodes);
    });
    return resultData;
  };
  const lastNodes = findLastNode(originData);
  // const treeDataFlatted = flattenTreeData(originData);
  const categoryTreeNodes: ITreeData[][] = generateCategoryTreeNodes(
    lastNodes,
    originData,
  );
  const result: ITodoCategory[] = [];
  // 将 categoryTreeNodes 转换成 TodoCategory格式数组
  categoryTreeNodes.forEach((treeNodes) => {
    const items = treeNodes.map((node) => {
      return {
        id: node.key,
        name: node.name.trim() ? node.name.trim() : '/',
      };
    });
    const values = treeNodes.map((node) => node.value);
    const formatTypes = treeNodes.map((node) => node.formatType);
    while (items.length > 0 && items.length < 3) {
      items.push({
        id: generateUUID(),
        name: '/',
      });
    }
    const formatType = formatTypes.pop();
    result.push({
      items,
      value: values.pop() || null,
      formatType: formatType as number,
    });
  });
  return result;
};

export const todoTypeTool = new TodoTypeManageClass();
